import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

const ThemeContext = createContext();

const FONT_SIZE_OPTIONS = ["small", "medium", "large"];
const FONT_SIZE_BASE_PX = { small: 14, medium: 16, large: 18 };

export const ThemeProvider = ({ children }) => {
  // Dark mode state with localStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? JSON.parse(savedTheme) : true; // Default to dark mode
  });

  // Font size: small (14px), medium (16px), large (18px)
  const [fontSize, setFontSizeState] = useState(() => {
    try {
      const saved = localStorage.getItem("tidleFontSize");
      return saved && FONT_SIZE_OPTIONS.includes(saved) ? saved : "medium";
    } catch {
      return "medium";
    }
  });

  // Experimental features state with localStorage persistence
  const [isExperimentalFeaturesEnabled, setIsExperimentalFeaturesEnabled] = useState(() => {
    const savedExperimentalFeatures = localStorage.getItem("experimentalFeatures");
    return savedExperimentalFeatures ? JSON.parse(savedExperimentalFeatures) : false; // Default to disabled
  });

  const fontSizeBasePx = FONT_SIZE_BASE_PX[fontSize];

  // Save dark mode preference to localStorage and update UI
  useEffect(() => {
    localStorage.setItem("theme", JSON.stringify(isDarkMode));

    // Update the theme-color meta tag
    const themeColor = isDarkMode ? "#171717" : "#ffffff";
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", themeColor);
    }

    // Update the apple-mobile-web-app-status-bar-style meta tag
    const appleStatusBarStyle = document.querySelector("meta[name=apple-mobile-web-app-status-bar-style]");
    if (appleStatusBarStyle) {
      appleStatusBarStyle.setAttribute("content", isDarkMode ? "default" : "black-translucent");
    }

    // Update the background color of the body
    document.body.style.backgroundColor = isDarkMode ? "#171717" : "#ffffff";
  }, [isDarkMode]);

  // Persist font size and apply to document root so rem-based and scaled UI respond
  useEffect(() => {
    try {
      localStorage.setItem("tidleFontSize", fontSize);
    } catch {
      // ignore
    }
    document.documentElement.style.setProperty("--tidle-font-size-base", `${fontSizeBasePx}px`);
  }, [fontSize, fontSizeBasePx]);

  // Save experimental features preference to localStorage
  useEffect(() => {
    localStorage.setItem("experimentalFeatures", JSON.stringify(isExperimentalFeaturesEnabled));
  }, [isExperimentalFeaturesEnabled]);

  // Toggle dark mode
  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  // Set font size (small | medium | large)
  const setFontSize = (value) => {
    if (FONT_SIZE_OPTIONS.includes(value)) setFontSizeState(value);
  };

  // Cycle font size for A- / A+ buttons
  const cycleFontSize = (direction) => {
    setFontSizeState((prev) => {
      const i = FONT_SIZE_OPTIONS.indexOf(prev) + (direction === "up" ? 1 : -1);
      const next = (i + FONT_SIZE_OPTIONS.length) % FONT_SIZE_OPTIONS.length;
      return FONT_SIZE_OPTIONS[next];
    });
  };

  // Toggle experimental features
  const toggleExperimentalFeatures = () => {
    setIsExperimentalFeaturesEnabled((prev) => !prev);
  };

  const value = useMemo(
    () => ({
      isDarkMode,
      toggleTheme,
      isExperimentalFeaturesEnabled,
      toggleExperimentalFeatures,
      fontSize,
      setFontSize,
      cycleFontSize,
      fontSizeBasePx,
      fontSizeOptions: FONT_SIZE_OPTIONS,
    }),
    [
      isDarkMode,
      isExperimentalFeaturesEnabled,
      fontSize,
      fontSizeBasePx,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};